import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

interface AlertSettingsProps {
  employeeId?: string;
}

interface AlertSetting {
  id?: string;
  employee_id: string;
  interval_minutes: number;
  work_start_time: string;
  work_end_time: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

const AlertSettings: React.FC<AlertSettingsProps> = ({ employeeId }) => {
  const [settings, setSettings] = useState<AlertSetting | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (employeeId) {
      fetchSettings();
    }
  }, [employeeId]);

  const fetchSettings = async () => {
    if (!employeeId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Fetching settings for employee:', employeeId);
      
      // Primero, intentamos obtener las configuraciones existentes
      const { data: existingSettings, error: fetchError } = await supabase
        .from('alert_settings')
        .select()
        .eq('employee_id', employeeId);

      console.log('Fetch response:', { existingSettings, fetchError });

      if (fetchError) {
        console.error('Error fetching settings:', fetchError);
        setError(fetchError.message);
        return;
      }

      // Si encontramos configuraciones existentes
      if (existingSettings && existingSettings.length > 0) {
        console.log('Found existing settings:', existingSettings[0]);
        setSettings(existingSettings[0]);
        return;
      }

      // Si no hay configuraciones, creamos unas por defecto
      console.log('No settings found, creating default settings');
      const defaultSettings: AlertSetting = {
        employee_id: employeeId,
        interval_minutes: 30,
        work_start_time: '09:00',
        work_end_time: '18:00',
        active: true
      };
      
      const { data: newSettings, error: insertError } = await supabase
        .from('alert_settings')
        .insert([defaultSettings])
        .select()
        .single();

      if (insertError) {
        console.error('Error creating default settings:', insertError);
        setError(insertError.message);
        return;
      }

      if (newSettings) {
        console.log('Created default settings:', newSettings);
        setSettings(newSettings);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings || !employeeId) return;

    try {
      setError(null);
      setSuccessMessage(null);

      const updateData = {
        ...settings,
        employee_id: employeeId,
        updated_at: new Date().toISOString()
      };

      console.log('Updating settings with data:', updateData);

      const { error: updateError } = await supabase
        .from('alert_settings')
        .upsert(updateData);

      if (updateError) {
        console.error('Error updating settings:', updateError);
        setError(updateError.message);
      } else {
        console.log('Settings updated successfully');
        setSuccessMessage('Settings updated successfully');
        setSettings(updateData);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('Unexpected error during update:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading settings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-50 rounded-md">
        <p className="font-medium">Error</p>
        <p className="text-sm">{error}</p>
        <button 
          onClick={fetchSettings}
          className="mt-2 text-sm text-indigo-600 hover:text-indigo-500"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="p-4 text-center text-gray-500">
        <p>No settings found.</p>
        <button 
          onClick={fetchSettings}
          className="mt-2 text-sm text-indigo-600 hover:text-indigo-500"
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4">Alert Settings</h2>
      
      {successMessage && (
        <div className="mb-4 p-2 bg-green-50 text-green-700 rounded-md text-center">
          {successMessage}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Alert Interval (minutes)
          </label>
          <input
            type="number"
            name="interval_minutes"
            value={settings.interval_minutes}
            onChange={handleChange}
            min="1"
            max="480"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
          <p className="mt-1 text-sm text-gray-500">
            How often should we check for employee attention (1-480 minutes)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Work Start Time
          </label>
          <input
            type="time"
            name="work_start_time"
            value={settings.work_start_time}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Work End Time
          </label>
          <input
            type="time"
            name="work_end_time"
            value={settings.work_end_time}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            name="active"
            checked={settings.active}
            onChange={handleChange}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-900">
            Active
          </label>
          <p className="ml-2 text-sm text-gray-500">
            Enable/disable attention checks
          </p>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default AlertSettings;
